import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import type { FrameStatus } from 'components/annotation-page/workspace/steps/steps-api';
import { sharedThumbnailLoader } from 'utils/frame-thumbnails';
import { writeLatestFrame } from 'utils/remember-latest-frame';
import { t } from 'utils/i18n';
import { tiles, tileStatus } from './task-model';

interface Props {
    session: 'jobs' | 'reference_jobs';
    jobId: number;
    count: number;
    status?: Record<string, FrameStatus>;
    // where a tile opens; the frame is remembered so the workspace starts on it
    path: string;
    max?: number;
}

// the first frames of a job in one row; the workspace strip is the full gallery
export default function FrameTiles({
    session, jobId, count, status, path, max = 6,
}: Props): JSX.Element {
    const { shown, more } = tiles(count, max);
    const [thumbs, setThumbs] = useState<Record<number, string>>({});
    // a thumbnail that failed twice stops shimmering: the tile still opens the drawing
    const [failed, setFailed] = useState<Record<number, boolean>>({});

    useEffect(() => {
        let cancelled = false;
        const timers: number[] = [];
        const loader = sharedThumbnailLoader(session, jobId);
        const load = (frame: number, retry: boolean): void => {
            loader.load(frame)
                .then((url) => { if (!cancelled) setThumbs((prev) => ({ ...prev, [frame]: url })); })
                .catch(() => {
                    if (cancelled) return;
                    if (retry) timers.push(window.setTimeout(() => load(frame, false), 1500));
                    else setFailed((prev) => ({ ...prev, [frame]: true }));
                });
        };
        shown.forEach((frame) => load(frame, true));
        return () => {
            cancelled = true;
            timers.forEach((timer) => window.clearTimeout(timer));
        };
    }, [session, jobId, shown.length]);

    return (
        <div className='cvat-frame-tiles'>
            {shown.map((frame) => {
                const frameStatus = tileStatus(status, frame);
                return (
                    <Link
                        key={frame}
                        to={path}
                        className='cvat-frame-tile'
                        aria-label={t('Open {n}', { n: frame + 1 })}
                        onClick={() => writeLatestFrame(jobId, frame)}
                    >
                        {thumbs[frame] ? <img src={thumbs[frame]} alt='' /> : <span className={`cvat-frame-tile-placeholder${failed[frame] ? '' : ' cvat-preview-skeleton'}`} />}
                        {frameStatus && <span className={`cvat-frame-tile-dot cvat-frame-tile-dot-${frameStatus}`} />}
                        <span className='cvat-frame-tile-number'>{frame + 1}</span>
                    </Link>
                );
            })}
            {more > 0 && (
                <Link to={path} className='cvat-frame-tile cvat-frame-tile-more' aria-label={t('{n} more in the workspace', { n: more })}>
                    <strong>{`+${more}`}</strong>
                    {t('more')}
                </Link>
            )}
        </div>
    );
}
